import {match} from 'ts-pattern';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {CustomerMessageAttachmentResponseBody} from '@dms/api/core';

// Content types every major browser renders inline once the storage service serves them with
// `type=inline`. Deliberately excludes text/html and image/svg+xml — attachments come from inbound
// (untrusted) email and both formats execute script once opened, so they stay download-only.
const PREVIEWABLE_CONTENT_TYPES = [
  'application/pdf',
  'audio/mpeg',
  'audio/ogg',
  'audio/wav',
  'audio/webm',
  'image/avif',
  'image/bmp',
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/webp',
  'text/plain',
  'video/mp4',
  'video/ogg',
  'video/webm',
];

// Extensions of the content types above — the fallback for senders whose mail client omitted the
// content type or reported a generic binary one.
const PREVIEWABLE_EXTENSIONS = [
  'avif',
  'bmp',
  'gif',
  'jpeg',
  'jpg',
  'mp3',
  'mp4',
  'ogg',
  'pdf',
  'png',
  'txt',
  'wav',
  'webm',
  'webp',
];

// Placeholders senders use when they do not know the real type — they say nothing about the file.
const GENERIC_CONTENT_TYPES = ['application/octet-stream', 'binary/octet-stream'];

const PDF_CONTENT_TYPE = 'application/pdf';
const FILENAME_QUERY_PARAM = 'filename';
// Kept in sync with getFilePreviewUrl, which only converts when the filename query param is a doc.
const CONVERTIBLE_FILENAME_REGEX = /\.docx?/;

const CONTENT_TYPE_PARAMETER_SEPARATOR = ';';
const EXTENSION_SEPARATOR = '.';
const NOT_FOUND_INDEX = -1;

function getNormalizedContentType(contentType: string | null): string {
  return contentType?.split(CONTENT_TYPE_PARAMETER_SEPARATOR)[0]?.trim().toLowerCase() ?? '';
}

function getFileExtension(filename: string): string {
  const trimmedFilename = filename.trim();
  const separatorIndex = trimmedFilename.lastIndexOf(EXTENSION_SEPARATOR);

  return separatorIndex === NOT_FOUND_INDEX
    ? ''
    : trimmedFilename.slice(separatorIndex + 1).toLowerCase();
}

/**
 * The storage service converts Word documents to PDF on the fly, but getFilePreviewUrl only builds
 * that conversion URL when the attachment URL carries a document filename query param. Without it
 * the preview would silently download the .docx instead, so it is not offered.
 */
function isConvertibleToPdf(uri: string, contentType: string): boolean {
  const filename = new URL(uri).searchParams.get(FILENAME_QUERY_PARAM) ?? '';

  return CONVERTIBLE_FILENAME_REGEX.test(filename) && !contentType.startsWith(PDF_CONTENT_TYPE);
}

/**
 * Decides whether an attachment can be opened in a browser tab instead of being saved to disk.
 * The reported content type wins; the filename extension only steps in when that type is missing
 * or generic.
 *
 * Expects a URL that already passed isSafeAttachmentUrl — it parses the URL to detect documents the
 * storage service converts to PDF for preview.
 */
export function isPreviewableAttachment(
  attachment: CustomerMessageAttachmentResponseBody
): boolean {
  const contentType = getNormalizedContentType(attachment.contentType);
  const isContentTypeConclusive =
    isNotNilOrEmpty(contentType) && !GENERIC_CONTENT_TYPES.includes(contentType);

  const isRenderedByBrowser = match(isContentTypeConclusive)
    .with(true, () => PREVIEWABLE_CONTENT_TYPES.includes(contentType))
    .otherwise(() => PREVIEWABLE_EXTENSIONS.includes(getFileExtension(attachment.filename)));

  return isRenderedByBrowser || isConvertibleToPdf(attachment.uri, contentType);
}
